import React from 'react';
import { ActionIcon, Button, Drawer, Flex } from '@mantine/core';
import { FormProvider, useWatch } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { PersonRelationType, PersonType } from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { makeRelatedPersonTypeOptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/relatedPersonsSection.utils.ts';
import { useAddRelatedPersonDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/AddRelatedPersonDrawer/useAddRelatedPersonDrawer.ts';
import { ADD_RELATED_PERSON_FORM_FIELDS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/AddRelatedPersonDrawer/addRelatedPersonDrawer.types.ts';

interface Props {
	opened: boolean;
	hasPermission: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

export const AddRelatedPersonDrawer = ({ opened, hasPermission, onClose, onSuccess }: Props) => {
	const tCapManage = useTranslationCapManage();

	const { form, onLookup, onSubmit, matchedExisting, isLookingUp, canSubmit } =
		useAddRelatedPersonDrawer({
			opened,
			onClose,
			onSuccess,
		});

	const relationTypeOptions = makeRelatedPersonTypeOptions(tCapManage, PersonType.LEGAL);

	const watchedRelationType = useWatch({
		control: form.control,
		name: ADD_RELATED_PERSON_FORM_FIELDS.relation_type,
	});

	// A named role describes itself, so the free-text label resets on switch —
	// otherwise a stale role would linger and reappear on switching back.
	const onRelationTypeChange = (value: string | null) => {
		if (value !== PersonRelationType.OTHER) {
			form.setValue(ADD_RELATED_PERSON_FORM_FIELDS.custom_relation_type, '');
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tCapManage('related_persons.drawer.add_title')}
		>
			<FormProvider {...form}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<TextInput
							name={ADD_RELATED_PERSON_FORM_FIELDS.email}
							control={form.control}
							label={tCapManage('related_persons.drawer.form.email_label')}
							placeholder={tCapManage(
								'related_persons.drawer.form.email_placeholder'
							)}
							size="md"
							disabled={!hasPermission}
							rightSection={
								<ActionIcon
									onClick={onLookup}
									loading={isLookingUp}
									disabled={!hasPermission}
									c="dark.0"
								>
									<Icon variant="search-v3" size="lg" />
								</ActionIcon>
							}
						/>

						<TextInput
							name={ADD_RELATED_PERSON_FORM_FIELDS.first_name}
							control={form.control}
							label={tCapManage('related_persons.drawer.form.first_name_label')}
							size="md"
							disabled={!hasPermission || matchedExisting}
						/>
						<TextInput
							name={ADD_RELATED_PERSON_FORM_FIELDS.last_name}
							control={form.control}
							label={tCapManage('related_persons.drawer.form.last_name_label')}
							size="md"
							disabled={!hasPermission || matchedExisting}
						/>
						<Select
							name={ADD_RELATED_PERSON_FORM_FIELDS.relation_type}
							control={form.control}
							label={tCapManage('related_persons.drawer.form.relation_type_label')}
							placeholder={tCapManage(
								'related_persons.drawer.form.relation_type_placeholder'
							)}
							data={relationTypeOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							searchable
							size="md"
							disabled={!hasPermission}
							onChange={onRelationTypeChange}
						/>
						{watchedRelationType === PersonRelationType.OTHER && (
							<TextInput
								name={ADD_RELATED_PERSON_FORM_FIELDS.custom_relation_type}
								control={form.control}
								label={tCapManage(
									'related_persons.drawer.form.custom_relation_type_label'
								)}
								placeholder={tCapManage(
									'related_persons.drawer.form.custom_relation_type_placeholder'
								)}
								size="md"
								disabled={!hasPermission}
							/>
						)}
					</Flex>

					<Button onClick={form.handleSubmit(onSubmit)} disabled={!canSubmit}>
						{tCapManage('related_persons.drawer.add_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
